import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 168, 232, 0.4); }
  50% { box-shadow: 0 0 20px 5px rgba(0, 168, 232, 0.2); }
`;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: linear-gradient(180deg, #0a1628 0%, #0d2137 50%, #0a1628 100%);
  color: #ffffff;
  font-family: 'Montserrat', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Content = styled.div`
  max-width: 600px;
  width: 100%;
  animation: ${fadeIn} 0.6s ease-out;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Banner = styled.img`
  width: 100%;
  height: auto;
  display: block;
  
  &.top {
    border-radius: 12px 12px 0 0;
  }
  
  &.bottom {
    border-radius: 0 0 12px 12px;
  }
`;

const FormContainer = styled.div`
  background: linear-gradient(180deg, #0d2137 0%, #112840 100%);
  padding: 2rem 2.5rem;
  border-left: 1px solid rgba(0, 168, 232, 0.3);
  border-right: 1px solid rgba(0, 168, 232, 0.3);
  
  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem;
  }
`;

const FormTitle = styled.h2`
  color: #ffffff;
  text-align: center;
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 1px;
`;

const FormSubtitle = styled.p`
  color: #00a8e8;
  text-align: center;
  margin: 0 0 1.5rem 0;
  font-size: 0.95rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 0.9rem 1rem;
  border: 1px solid rgba(0, 168, 232, 0.4);
  border-radius: 6px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    outline: none;
    border-color: #00a8e8;
    background: rgba(0, 168, 232, 0.1);
    box-shadow: 0 0 0 3px rgba(0, 168, 232, 0.15);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #00a8e8 0%, #0077b6 100%);
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 2px;
  animation: ${pulse} 2s infinite;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 168, 232, 0.4);
    background: linear-gradient(135deg, #00b4d8 0%, #0088cc 100%);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    animation: none;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(0, 168, 232, 0.15);
  border: 1px solid rgba(0, 168, 232, 0.5);
  color: #00d4ff;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease-out;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const CasaPradoResidence = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Send data to the webhook
      const response = await fetch('https://webhook-editor.abianca.com.br/webhook/casa-prado-4d77-83c5-df81c4ee25f5', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          source: 'Casa Prado Residence Landing Page',
          timestamp: new Date().toISOString()
        }),
      });

      if (!response.ok) {
        throw new Error('Erro ao enviar os dados');
      }
      
      const result = await response.json();
      console.log('Success:', result);
      
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Content>
        <Banner 
          src="/[Direcional] Banner LP Acesso Premiado.png" 
          alt="Promoção Acesso Premiado RIVA" 
          className="top"
        />
        
        <FormContainer>
          <FormTitle>Preencha seus dados</FormTitle>
          <FormSubtitle>E participe da promoção</FormSubtitle>
          
          {isSubmitted ? (
            <SuccessMessage>
              ✓ Obrigado pelo seu interesse! Entraremos em contato em breve.
            </SuccessMessage>
          ) : (
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Nome Completo</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Digite seu nome completo"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Digite seu melhor e-mail"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="(00) 00000-0000"
                />
              </FormGroup>
              
              <SubmitButton type="submit" disabled={isLoading}>
                {isLoading ? 'Enviando...' : 'Participar Agora'}
              </SubmitButton>
            </Form>
          )}
        </FormContainer>
      </Content>
    </Container>
  );
};

export default CasaPradoResidence;
